import React from 'react'
import PropTypes from 'prop-types'
import T from 'i18n-react'
import classNames from 'classnames'
import { validate } from 'devx-js-utilities'

export class TextInput extends React.Component {
	static propTypes = {
		canBeEmpty: PropTypes.bool,
		className: PropTypes.string,
		disabled: PropTypes.bool,
		isCity: PropTypes.bool,
		isEmail: PropTypes.bool,
		isInteger: PropTypes.bool,
		isLandRegistryNumber: PropTypes.bool,
		isNumber: PropTypes.bool,
		isPhone: PropTypes.bool,
		isRequired: PropTypes.bool,
		isStreet: PropTypes.bool,
		isSurname: PropTypes.bool,
		isZipCode: PropTypes.bool,
		label: PropTypes.string.isRequired,
		labelNote: PropTypes.string,
		maxLength: PropTypes.number,
		min: PropTypes.number,
		minLength: PropTypes.number,
		name: PropTypes.string.isRequired,
		onChange: PropTypes.func.isRequired,
		onErrorChange: PropTypes.func,
		placeholder: PropTypes.string,
		range: PropTypes.object,
		regExp: PropTypes.any,
		required: PropTypes.bool,
		type: PropTypes.string,
		unit: PropTypes.string,
		unitExponent: PropTypes.string,
		validationErrors: PropTypes.object,
		value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		wrapperClassName: PropTypes.string,
	}

	static defaultProps = {
		canBeEmpty: false,
		className: 'textInput',
		disabled: false,
		errorStyle: {},
		floatingLabelFixed: false,
		floatingLabelFocusStyle: null,
		floatingLabelShrinkStyle: null,
		floatingLabelStyle: null,
		floatingLabelText: null,
		isEmail: false,
		isInteger: false,
		isCity: false,
		isLandRegistryNumber: false,
		isStreet: false,
		isSurname: false,
		isNumber: false,
		isPhone: false,
		isZipCode: false,
		isRequired: false,
		label: 'Input label',
		labelNote: '',
		maxLength: Number.MAX_SAFE_INTEGER,
		min: null,
		minLength: 0,
		onErrorChange: () => null,
		placeholder: '',
		range: { start: null, end: null },
		regExp: null,
		required: true,
		type: 'text',
		unit: '',
		unitExponent: '',
		validationErrors: {},
		wrapperClassName: '',
	}

	isValid = () =>
		this.validate(true, this.props.value)

	validate = (showError = true, value = this.props.value) => {
		if (this.props.canBeEmpty && value.length === 0) {
			// just for no validating empty field
		} else if (this.props.isRequired && value.length <= 0) {
			showError && this.props.onErrorChange(this.props.name, T.translate('validationErrors.required'))
			return false
		} else if (this.props.regExp && !this.props.regExp.test(value)) {
			showError && this.props.onErrorChange(this.props.name, T.translate('validationErrors.badFormat'))
			return false
		} else if (this.props.minLength > 0 && value.length < this.props.minLength) {
			showError && this.props.onErrorChange(this.props.name, T.translate('validationErrors.tooShort', { length: this.props.minLength }))
			return false
		} else if (this.props.maxLength > 0 && value.length > this.props.maxLength) {
			showError && this.props.onErrorChange(this.props.name, T.translate('validationErrors.tooLong', { length: this.props.maxLength }))
			return false
		} else if (this.props.isPhone && !validate.validatePhone(value)) {
			showError && this.props.onErrorChange(this.props.name, T.translate('validationErrors.badPhoneFormat'))
			return false
		} else if (this.props.isEmail && !validate.validateEmail(value)) {
			showError && this.props.onErrorChange(this.props.name, T.translate('validationErrors.email'))
			return false
		} else if (this.props.isZipCode && !validate.validateZipCode(value)) {
			showError && this.props.onErrorChange(this.props.name, T.translate('validationErrors.badZipCode'))
			return false
		} else if (this.props.isCity && !validate.validateCity(value)) {
			showError && this.props.onErrorChange(this.props.name, T.translate('validationErrors.badCityFormat'))
			return false
		} else if (this.props.isStreet && !validate.validateStreet(value)) {
			showError && this.props.onErrorChange(this.props.name, T.translate('validationErrors.badStreetFormat'))
			return false
		} else if (this.props.isSurname && !validate.validateSurname(value)) {
			showError && this.props.onErrorChange(this.props.name, T.translate('validationErrors.badSurnameFormat'))
			return false
		} else if (this.props.isLandRegistryNumber && !validate.validateLandRegistryNumber(value)) {
			showError && this.props.onErrorChange(this.props.name, T.translate('validationErrors.badLandRegistryNumberFormat'))
			return false
		} else if (this.props.isNumber && this.props.min && Number(value) < Number(this.props.min)) {
			let error = T.translate(`validationErrors.${this.props.name}.min`, { min: this.props.min })
			if (!error) {
				error = T.translate('validationErrors.lessThanMin', { min: this.props.min })
			}

			showError && this.props.onErrorChange(this.props.name, error)
			return false
		} else if (this.props.isNumber && this.props.range.start !== null &&
			this.props.range.end !== null && (this.props.range.start > parseInt(value, 10) ||
				this.props.range.end < parseInt(value, 10))) {
			showError && this.props.onErrorChange(this.props.name, T.translate('validationErrors.range', this.props.range))
			return false
		} else if (this.props.isNumber && !validate.isNumeric(value)) {
			showError && this.props.onErrorChange(this.props.name, T.translate('validationErrors.numeric'))
			return false
		} else if (this.props.isInteger && !/^\d+$/.test(value)) {
			showError && this.props.onErrorChange(this.props.name, T.translate('validationErrors.integer'))
			return false
		}

		this.props.onErrorChange(this.props.name, undefined) // eslint-disable-line
		return true
	}

	hasError = () =>
		this.props.validationErrors[this.props.name] !== undefined // eslint-disable-line


	handleOnChange = (event) => {
		this.props.onChange(event.target.value)
	}

	handleOnBlur = (event) => {
		this.validate(true, event.target.value)
	}

	render() {

		const inputClasses = [
			'form-control',
			{
				'is-invalid': this.hasError()
			}
		]

		let attributes = {
			className: classNames(inputClasses),
			disabled: this.props.disabled,
			id: this.props.name,
			maxLength: this.props.maxLength,
			name: this.props.name,
			onBlur: this.handleOnBlur,
			onChange: this.handleOnChange,
			required: this.props.required,
			type: this.props.type,
			value: this.props.value
		}

		if (this.props.type === 'number' && this.props.min) {
			attributes['min'] = this.props.min
		}

		return (
			<div className='form-group d-flex flex-row flex-wrap flex-sm-row align-items-center'>
				<div className='col-10 col-sm-4 order-0 order-sm-0 pt-sm-2 mt-sm-1 text-sm-right'>
					<label className='mb-0 mr-4 font-weight-bold' htmlFor={this.props.name}>
						{this.props.label}
						{this.props.required && <span className='text-danger'>&nbsp;*</span>}
						<span className='note'>&nbsp;{this.props.labelNote} </span>
					</label>
				</div>
				<div className='col-12 col-sm-6 col-lg-4 order-3 order-sm-0 mt-2 mt-sm-0 pr-sm-0'>
					<div className='w-100 position-relative'>
						<input
							{...attributes}
						/>
						{this.props.validationErrors[this.props.name] &&
						<div className='invalid-tooltip mt-0'>
							<span> {this.props.validationErrors[this.props.name]} </span>
						</div>
						}
					</div>
				</div>
				<div className='col-2 col-sm-2 order-2 order-sm-0 mt-2 mt-sm-0 py-sm-2 pr-sm-0'>
					{this.props.unit &&
					<span>
								{this.props.unit}
						{this.props.unitExponent && <sup>{this.props.unitExponent}</sup>}
							</span>
					}
				</div>
			</div>
		)
	}
}

export default TextInput
